'use client';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, Radio } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

function rec(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}
function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/**
 * The legacy sandbox snapshot view, deliberately demoted to a secondary,
 * clearly-labeled section. Canonical files are the source of truth (above);
 * anything living only on the live sandbox disk is "not yet synced".
 */
export function LegacySandboxSection({ output }: { output?: unknown }) {
  const [open, setOpen] = useState(false);
  const out = rec(output);
  const sandbox = rec(out?.sandbox);
  const previewUrl = str(out?.url) ?? str(rec(out?.preview)?.url);
  const name = str(sandbox?.name) ?? str(out?.name);
  const state = str(sandbox?.state);
  const language = str(sandbox?.language);
  const port = out?.port ?? sandbox?.port;

  const hasSandbox = Boolean(out && (previewUrl || name || state || port));

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="shrink-0 border-t border-border">
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/30">
        <Radio className={cn('size-3.5', hasSandbox ? 'text-amber-500' : 'text-muted-foreground')} />
        <span className="font-medium">Live sandbox (not yet synced)</span>
        <span className="text-[11px]">
          {hasSandbox ? state ?? 'connected' : 'no live sandbox'}
        </span>
        <ChevronDown className={cn('ml-auto size-3.5 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 pt-1 text-xs text-muted-foreground">
          <p className="mb-2">
            Files written directly on the live sandbox disk are not part of the canonical
            store until synced. Use “Snapshot from sandbox” to pull them in.
          </p>
          {hasSandbox ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              {name && (
                <>
                  <dt className="text-foreground">Sandbox</dt>
                  <dd className="truncate">{name}</dd>
                </>
              )}
              {state && (
                <>
                  <dt className="text-foreground">State</dt>
                  <dd>{state}</dd>
                </>
              )}
              {language && (
                <>
                  <dt className="text-foreground">Runtime</dt>
                  <dd>{language}</dd>
                </>
              )}
              {port != null && (
                <>
                  <dt className="text-foreground">Port</dt>
                  <dd>{String(port)}</dd>
                </>
              )}
              {previewUrl && (
                <>
                  <dt className="text-foreground">Preview</dt>
                  <dd className="truncate">
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-violet-300 underline underline-offset-2"
                    >
                      {previewUrl}
                    </a>
                  </dd>
                </>
              )}
            </dl>
          ) : (
            <p>No live sandbox is attached to this workspace right now.</p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
